import {
  Box, Typography, Chip, Stack, TextField, Divider,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import HistoryIcon from "@mui/icons-material/History";
import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { useCycleFilter } from "../cycleFilterContext";
import { DataTableShell } from "../components/table/DataTableShell";
import { CountingFilterSelect } from "../components/table/CountingFilterSelect";
import {
  DocumentCompareDrawer,
  DetailsJsonBlock,
} from "../components/viewer/DocumentCompareDrawer";

const AGENT_IDS = [
  "agent_1",
  "agent_2",
  "agent_3",
  "agent_4",
  "agent_5",
  "orchestrator",
];

const ENTITY_TYPES = [
  "transaction",
  "invoice",
  "match",
  "rule",
  "file",
  "cycle",
  "system",
  "qb_command",
  "vendor",
  "glAccount",
  "card",
];

const severityColor: Record<string, "default" | "warning" | "error"> = {
  info: "default",
  warning: "warning",
  error: "error",
};

function formatTimestamp(ts: number) {
  return new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncate(text: string, max: number) {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max) + "...";
}

const columns: GridColDef[] = [
  {
    field: "_creationTime",
    headerName: "Timestamp",
    width: 150,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Typography variant="body2" sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
        {formatTimestamp(p.value as number)}
      </Typography>
    ),
  },
  {
    field: "agentId",
    headerName: "Agent",
    width: 130,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Chip
        size="small"
        label={p.value}
        variant="outlined"
        sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
      />
    ),
  },
  {
    field: "action",
    headerName: "Action",
    flex: 1,
    minWidth: 160,
    sortable: true,
  },
  {
    field: "entityType",
    headerName: "Entity Type",
    width: 130,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Chip size="small" label={p.value} variant="outlined" />
    ),
  },
  {
    field: "entityId",
    headerName: "Entity ID",
    width: 140,
    sortable: false,
    renderCell: (p: GridRenderCellParams) => (
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
        title={p.value as string}
      >
        {truncate(p.value as string, 16)}
      </Typography>
    ),
  },
  {
    field: "severity",
    headerName: "Severity",
    width: 110,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Chip
        size="small"
        label={p.value}
        color={severityColor[p.value as string] ?? "default"}
      />
    ),
  },
  {
    field: "details",
    headerName: "Details",
    flex: 2,
    minWidth: 240,
    sortable: false,
    renderCell: (p: GridRenderCellParams) => (
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
        title={p.value as string}
      >
        {truncate(p.value as string, 100)}
      </Typography>
    ),
  },
];

export function AuditTrail() {
  const [searchParams] = useSearchParams();
  const entityParam = searchParams.get("entity") ?? "all";
  const idParam = searchParams.get("id") ?? "";

  const [agentFilter, setAgentFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState(
    ENTITY_TYPES.includes(entityParam) ? entityParam : "all"
  );
  const [entityIdFilter, setEntityIdFilter] = useState(idParam);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<any | null>(null);
  const { cycleArg } = useCycleFilter();

  const data = useStableQuery(api.auditLog.listFiltered, {
    ...cycleArg,
    agentId: agentFilter === "all" ? undefined : agentFilter,
    severity: severityFilter === "all" ? undefined : (severityFilter as any),
    entityType: entityFilter === "all" ? undefined : (entityFilter as any),
    limit: 100,
  }, []);

  const txnDocs = useQuery(
    api.transactions.getDocumentsForCompare,
    selectedEntry?.entityType === "transaction"
      ? { id: selectedEntry.entityId as any }
      : "skip",
  );
  const invoiceDocs = useQuery(
    api.invoices.getDocumentsForCompare,
    selectedEntry?.entityType === "invoice"
      ? { id: selectedEntry.entityId as any }
      : "skip",
  );

  const allRows: any[] = data ?? [];

  const filtered = useMemo(() => {
    let rows = allRows;
    if (entityIdFilter) {
      rows = rows.filter((r: any) => r.entityId === entityIdFilter);
    }
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(
      (log: any) =>
        log.action?.toLowerCase().includes(term) ||
        log.details?.toLowerCase().includes(term),
    );
  }, [allRows, searchTerm, entityIdFilter]);

  const agentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const id of AGENT_IDS) counts[id] = 0;
    for (const r of allRows) {
      if (counts[r.agentId] !== undefined) counts[r.agentId]++;
    }
    return counts;
  }, [allRows]);

  const severityCounts = useMemo(() => {
    const counts: Record<string, number> = { info: 0, warning: 0, error: 0 };
    for (const r of allRows) {
      if (counts[r.severity] !== undefined) counts[r.severity]++;
    }
    return counts;
  }, [allRows]);

  const entityCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of ENTITY_TYPES) counts[t] = 0;
    for (const r of allRows) {
      if (counts[r.entityType] !== undefined) counts[r.entityType]++;
    }
    return counts;
  }, [allRows]);

  const rows = filtered.map((log: any) => ({ ...log, id: log._id }));

  const resetFilters = () => {
    setAgentFilter("all");
    setSeverityFilter("all");
    setEntityFilter("all");
    setEntityIdFilter("");
    setSearchTerm("");
  };

  const docs =
    selectedEntry?.entityType === "transaction"
      ? txnDocs
      : selectedEntry?.entityType === "invoice"
        ? invoiceDocs
        : null;
  const isCompareMode =
    selectedEntry?.entityType === "transaction" ||
    selectedEntry?.entityType === "invoice";

  return (
    <>
      <DataTableShell
        title="Audit Trail"
        subtitle="Complete history of all agent actions and system events"
        rows={rows}
        columns={columns}
        loading={data === undefined}
        totalBeforeFilters={allRows.length}
        itemLabel="entry"
        emptyTitle="No audit entries found"
        emptyDescription="Agent actions will be logged here as they process transactions."
        emptyIcon={<HistoryIcon sx={{ fontSize: 32, color: "var(--c-link-accent)" }} />}
        onResetFilters={resetFilters}
        gridProps={{
          initialState: {
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "_creationTime", sort: "desc" }] },
          },
          onRowClick: (params) => setSelectedEntry(params.row),
          sx: { "& .MuiDataGrid-row": { cursor: "pointer" } },
        }}
        filterBar={
          <>
            <TextField
              size="small"
              label="Search"
              placeholder="Action or details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <CountingFilterSelect
              label="Agent"
              value={agentFilter}
              onChange={setAgentFilter}
              allLabel="All Agents"
              options={AGENT_IDS.map((id) => ({
                value: id,
                label: id,
                count: agentCounts[id],
              }))}
            />
            <CountingFilterSelect
              label="Severity"
              value={severityFilter}
              onChange={setSeverityFilter}
              allLabel="All Severity"
              options={[
                { value: "info", label: "Info", count: severityCounts.info },
                { value: "warning", label: "Warning", count: severityCounts.warning },
                { value: "error", label: "Error", count: severityCounts.error },
              ]}
            />
            <CountingFilterSelect
              label="Entity Type"
              value={entityFilter}
              onChange={setEntityFilter}
              allLabel="All Entity Types"
              options={ENTITY_TYPES.map((t) => ({
                value: t,
                label: t,
                count: entityCounts[t],
              }))}
            />
            {entityIdFilter && (
              <Chip
                label={`Showing audit history for ${entityFilter} ${entityIdFilter}`}
                onDelete={() => setEntityIdFilter("")}
                size="small"
              />
            )}
          </>
        }
      />

      <DocumentCompareDrawer
        open={Boolean(selectedEntry)}
        onClose={() => setSelectedEntry(null)}
        title={selectedEntry?.action ?? "Audit Entry"}
        subtitle={
          selectedEntry
            ? `${selectedEntry.agentId} · ${selectedEntry.entityType} · ${formatTimestamp(selectedEntry._creationTime)}`
            : undefined
        }
        left={
          isCompareMode
            ? {
                storageId: docs?.bankStorageId ?? null,
                label: docs?.bankFileName
                  ? `Bank Statement · ${docs.bankFileName}`
                  : "Bank Statement",
              }
            : undefined
        }
        right={
          isCompareMode
            ? {
                storageId: docs?.invoiceStorageId ?? null,
                label: docs?.invoiceVendorName
                  ? `Invoice · ${docs.invoiceVendorName}${docs.invoiceNumber ? ` #${docs.invoiceNumber}` : ""}`
                  : "Invoice",
              }
            : undefined
        }
        editTarget={
          selectedEntry?.entityType === "transaction"
            ? { type: "transaction", id: selectedEntry.entityId }
            : selectedEntry?.entityType === "invoice"
              ? { type: "invoice", id: selectedEntry.entityId }
              : null
        }
        detailsContent={
          selectedEntry ? (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={selectedEntry.severity}
                  color={severityColor[selectedEntry.severity] ?? "default"}
                />
                <Typography variant="caption" color="text.secondary">
                  Entity ID:{" "}
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {selectedEntry.entityId}
                  </Box>
                </Typography>
                {selectedEntry.modelId && (
                  <Typography variant="caption" color="text.secondary">
                    Model: {selectedEntry.modelId}
                  </Typography>
                )}
              </Stack>
              {isCompareMode && <Divider sx={{ my: 1 }} />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Full Details
              </Typography>
              <DetailsJsonBlock data={selectedEntry.details} />
            </Box>
          ) : null
        }
      />
    </>
  );
}
